/**
 * Clientelo AI Dashboard Assistant (ChatGPT)
 * Voice input + data-only guardrail. Calls Netlify function (OpenAI).
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/useAuthStore';
import { supabase } from '../lib/supabase';

const SPEECH_LANG = 'es-CO';

const SUGGESTIONS = [
  '¿Qué citas tenemos hoy?',
  '¿Qué entregas hay hoy?',
  '¿Cuánto vendimos ayer?',
];

interface DataCardRow {
  label: string;
  value: string;
  highlight?: boolean;
}

interface DataCard {
  type: string;
  title?: string;
  rows?: DataCardRow[];
}

interface Message {
  role: 'user' | 'assistant';
  text: string;
  data_card: DataCard | null;
  ts: Date;
}

function DataCardDisplay({ card }: { card: DataCard | null }) {
  if (!card || card.type === 'none' || !card.rows?.length) return null;
  const icons: Record<string, string> = {
    sale: '🧾',
    inventory: '📦',
    customer: '👤',
    appointment: '📅',
    delivery: '🚚',
    summary: '📊',
  };
  return (
    <div
      style={{
        background: 'linear-gradient(135deg,#1a2744,#0f1b38)',
        border: '1px solid #2d4a8a',
        borderRadius: '10px',
        padding: '14px',
        marginTop: '10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <span>{icons[card.type] || '📋'}</span>
        <span
          style={{
            color: '#7eb3ff',
            fontSize: '11px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
          }}
        >
          {card.title || card.type}
        </span>
      </div>
      {card.rows.map((row, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 0',
            borderBottom: i < card.rows!.length - 1 ? '1px solid #1e2e50' : 'none',
          }}
        >
          <span style={{ color: '#8a9bb5', fontSize: '12.5px' }}>{row.label}</span>
          <span
            style={{
              color: row.highlight ? '#4ade80' : '#e2e8f0',
              fontWeight: row.highlight ? '700' : '500',
              fontSize: '12.5px',
            }}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function MicButton({
  onTranscript,
  disabled,
}: {
  onTranscript: (t: string) => void;
  disabled: boolean;
}) {
  const [listening, setListening] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      setUnsupported(true);
      return;
    }
    const r = new SR();
    r.lang = SPEECH_LANG;
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (e: SpeechRecognitionEvent) => {
      const t = e.results[0]?.[0]?.transcript?.trim() || '';
      if (t) onTranscript(t);
      setListening(false);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    return () => {
      try {
        recognitionRef.current?.abort();
      } catch {}
    };
  }, [onTranscript]);

  if (unsupported) return null;

  const toggle = () => {
    if (listening) {
      recognitionRef.current?.abort();
      setListening(false);
    } else {
      recognitionRef.current?.start();
      setListening(true);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      title={listening ? 'Toca para cancelar' : 'Habla tu pregunta (es-CO)'}
      style={{
        width: '38px',
        height: '38px',
        borderRadius: '10px',
        flexShrink: 0,
        border: listening ? '1px solid #ef4444' : '1px solid #1e3060',
        background: listening ? 'linear-gradient(135deg,#7f1d1d,#450a0a)' : 'transparent',
        color: listening ? '#fca5a5' : '#7eb3ff',
        cursor: disabled ? 'default' : 'pointer',
        fontSize: '15px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        animation: listening ? 'pulse-mic 1.2s infinite' : 'none',
      }}
    >
      {listening ? '⏹' : '🎤'}
    </button>
  );
}

// On Netlify: same-origin. For local dev set REACT_APP_AI_ASSISTANT_URL to your Netlify site + path, e.g. https://your-app.netlify.app/.netlify/functions/ai-assistant
const AI_ASSISTANT_URL =
  process.env.REACT_APP_AI_ASSISTANT_URL || '/.netlify/functions/ai-assistant';

export function AIAssistant() {
  const { currentEmpresa } = useAuthStore();
  const empresaId = currentEmpresa?.id && currentEmpresa.id !== 'sin-empresa' ? currentEmpresa.id : null;

  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      text: '¡Hola! Soy Diego, tu socio de IA. Puedo consultarte ventas, inventario, citas y entregas. Escribe o usa el micrófono 🎤 para preguntar.',
      data_card: null,
      ts: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<{ role: string; content: string }[]>([]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 120);
  }, [isOpen]);

  const sendMessage = useCallback(
    async (text?: string) => {
      const question = (text ?? input).trim();
      if (!question || loading) return;
      if (!empresaId) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: 'Selecciona una empresa para usar el asistente.',
            data_card: null,
            ts: new Date(),
          },
        ]);
        return;
      }
      setInput('');
      setMessages((prev) => [...prev, { role: 'user', text: question, data_card: null, ts: new Date() }]);
      setLoading(true);
      historyRef.current.push({ role: 'user', content: question });

      try {
        const now = new Date();
        const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;
        if (!accessToken) {
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', text: 'Tu sesión expiró. Vuelve a iniciar sesión.', data_card: null, ts: new Date() },
          ]);
          return;
        }
        const res = await fetch(AI_ASSISTANT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            question,
            history: historyRef.current.slice(-8),
            empresaId,
            localDate,
          }),
        });
        const raw = await res.text();
        let data: { response?: string; data_card?: DataCard } = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              text: res.ok ? 'Error de conexión. Intenta de nuevo.' : `Error del servidor (${res.status}). Revisa que OPENAI_API_KEY y SUPABASE_SERVICE_ROLE_KEY estén en Netlify.`,
              data_card: null,
              ts: new Date(),
            },
          ]);
          return;
        }
        const responseText = data.response ?? 'Error de conexión. Intenta de nuevo.';
        const dataCard = data.data_card ?? { type: 'none', rows: [] };
        historyRef.current.push({ role: 'assistant', content: responseText });
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', text: responseText, data_card: dataCard, ts: new Date() },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: 'Error de conexión. Por favor intenta nuevamente.',
            data_card: null,
            ts: new Date(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [input, loading, empresaId]
  );

  const handleTranscript = useCallback(
    (transcript: string) => {
      setInput(transcript);
      setTimeout(() => sendMessage(transcript), 80);
    },
    [sendMessage]
  );

  const fmt = (d: Date) => d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <button
        type="button"
        className="diego-fab"
        onClick={() => setIsOpen((o) => !o)}
        aria-label={isOpen ? 'Cerrar asistente' : 'Abrir asistente Diego'}
        style={{
          position: 'fixed',
          borderRadius: '50%',
          background: isOpen
            ? 'linear-gradient(135deg,#1e3a6e,#0d2040)'
            : 'linear-gradient(135deg,#2563eb,#1d4ed8)',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(37,99,235,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          transition: 'all 0.25s ease',
          transform: isOpen ? 'rotate(15deg)' : 'rotate(0deg)',
        }}
      >
        {isOpen ? '✕' : '✦'}
      </button>

      <div
        className="diego-panel"
        style={{
          position: 'fixed',
          background: '#080e1f',
          borderRadius: '16px',
          border: '1px solid #1e3060',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6),0 0 0 1px rgba(37,99,235,0.1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
          fontFamily: "'IBM Plex Sans','Segoe UI',sans-serif",
        }}
      >
        <div
          style={{
            padding: '16px 18px',
            background: 'linear-gradient(135deg,#0d1f45,#091530)',
            borderBottom: '1px solid #1e3060',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg,#2563eb,#1e40af)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              boxShadow: '0 0 12px rgba(37,99,235,0.4)',
            }}
          >
            ✦
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '14px' }}>
              Diego — Tu socio de IA
            </div>
            <div
              style={{
                color: '#4ade80',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#4ade80',
                  display: 'inline-block',
                  boxShadow: '0 0 6px #4ade80',
                }}
              />
              Solo datos de tu negocio
            </div>
          </div>
          <div
            style={{
              color: '#3d5075',
              fontSize: '10px',
              background: '#0d1626',
              padding: '3px 8px',
              borderRadius: '20px',
              border: '1px solid #1e2d50',
              whiteSpace: 'nowrap',
            }}
          >
            🔒 privado
          </div>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            scrollbarWidth: 'thin',
            scrollbarColor: '#1e3060 transparent',
          }}
        >
          {messages.map((msg, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <div
                style={{
                  maxWidth: '88%',
                  padding: '10px 13px',
                  borderRadius:
                    msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background:
                    msg.role === 'user'
                      ? 'linear-gradient(135deg,#2563eb,#1d4ed8)'
                      : 'linear-gradient(135deg,#111827,#0f172a)',
                  border: msg.role === 'user' ? 'none' : '1px solid #1e2d50',
                  color: '#e2e8f0',
                  fontSize: '13.5px',
                  lineHeight: '1.55',
                  boxShadow: msg.role === 'user' ? '0 4px 12px rgba(37,99,235,0.3)' : 'none',
                }}
              >
                {msg.text}
                {msg.role === 'assistant' && <DataCardDisplay card={msg.data_card} />}
              </div>
              <div
                style={{
                  color: '#3d5075',
                  fontSize: '10px',
                  marginTop: '4px',
                  paddingInline: '4px',
                }}
              >
                {fmt(msg.ts)}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex' }}>
              <div
                style={{
                  padding: '10px 16px',
                  borderRadius: '14px 14px 14px 4px',
                  background: 'linear-gradient(135deg,#111827,#0f172a)',
                  border: '1px solid #1e2d50',
                  display: 'flex',
                  gap: '5px',
                  alignItems: 'center',
                }}
              >
                {[0, 1, 2].map((n) => (
                  <div
                    key={n}
                    style={{
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      background: '#2563eb',
                      animation: 'bounce 1.2s infinite',
                      animationDelay: `${n * 0.2}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {messages.length <= 2 && (
          <div
            style={{
              padding: '0 12px 10px',
              display: 'flex',
              gap: '6px',
              overflowX: 'auto',
              scrollbarWidth: 'none',
            }}
          >
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => sendMessage(s)}
                style={{
                  whiteSpace: 'nowrap',
                  padding: '5px 10px',
                  borderRadius: '20px',
                  border: '1px solid #1e3060',
                  background: 'transparent',
                  color: '#7eb3ff',
                  fontSize: '11px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#0d1f45';
                  e.currentTarget.style.borderColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#1e3060';
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div
          style={{
            padding: '12px',
            borderTop: '1px solid #1e2d50',
            background: '#0a1020',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          <MicButton onTranscript={handleTranscript} disabled={loading} />
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Pregunta o usa el micrófono..."
            disabled={loading}
            style={{
              flex: 1,
              background: '#0d1626',
              border: '1px solid #1e3060',
              borderRadius: '10px',
              padding: '9px 13px',
              color: '#e2e8f0',
              fontSize: '13px',
              outline: 'none',
              transition: 'border-color 0.15s',
              minWidth: 0,
            }}
            onFocus={(e) => (e.target.style.borderColor = '#2563eb')}
            onBlur={(e) => (e.target.style.borderColor = '#1e3060')}
          />
          <button
            type="button"
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            style={{
              width: '38px',
              height: '38px',
              flexShrink: 0,
              borderRadius: '10px',
              background:
                input.trim() && !loading
                  ? 'linear-gradient(135deg,#2563eb,#1d4ed8)'
                  : '#0d1626',
              border: '1px solid #1e3060',
              color: input.trim() && !loading ? '#fff' : '#3d5075',
              cursor: input.trim() && !loading ? 'pointer' : 'default',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s',
            }}
          >
            ↑
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounce { 0%,80%,100%{transform:translateY(0);opacity:0.4} 40%{transform:translateY(-6px);opacity:1} }
        @keyframes pulse-mic { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.4)} 50%{box-shadow:0 0 0 8px rgba(239,68,68,0)} }
        .diego-fab {
          bottom: max(16px, env(safe-area-inset-bottom, 0px));
          right: 16px;
          width: 44px;
          height: 44px;
          z-index: 40;
        }
        .diego-panel {
          left: 12px;
          right: 12px;
          width: auto;
          bottom: 72px;
          max-height: min(70vh, 520px);
          z-index: 39;
        }
        @media (min-width: 1024px) {
          .diego-fab {
            bottom: 28px;
            right: 28px;
            width: 56px;
            height: 56px;
            z-index: 1000;
          }
          .diego-panel {
            left: auto;
            right: 28px;
            width: 360px;
            bottom: 96px;
            max-height: 590px;
            z-index: 999;
          }
        }
      `}</style>
    </>
  );
}

export default AIAssistant;
